import { type GeoMarker } from "common/components/MapContainer/types";
import {
  type AssetWithLocationFragment,
  type CurrencyCode,
  type PositionsListWithAssetLocations,
} from "common/types/mainTypes";
import { useMemo } from "react";
import { type LocationType } from "../types";
import { type ArenaPositions, type GeoMarkerWithPositionId } from "./types";
import { type AssetMarkerType } from "../../MultiManager/MapAndKpis/types";

export enum GeoErrorsCode {
  MissingLocation = "Missing location",
  GeocodingError = "Geocoding error",
  PoorSpecificity = "Poor specificity",
}

const checkNotNull = (entry: string | number | null): boolean => entry !== null;

export const getPossibleLocationError = (node: LocationType): string | null => {
  const entries = [node?.street ?? null, node?.postalCode ?? null, node?.city ?? null];

  const entriesFullAddress = [
    node?.street ?? null,
    node?.stateId ?? null,
    node?.postalCode ?? null,
    node?.city ?? null,
    node?.countryId ?? null,
  ];

  const hasSomeBasicAddress = entries.some(checkNotNull);
  const hasSomeFullAddress = entriesFullAddress.some(checkNotNull);

  const hasGeolocation = Boolean(node.latitude && node.longitude);

  if (!hasSomeBasicAddress) {
    if (hasGeolocation && hasSomeFullAddress) {
      // Some address fields and geolocation are present but basic address field are missing
      return GeoErrorsCode.PoorSpecificity;
    }

    if (!hasGeolocation && !hasSomeFullAddress) {
      // No geolocation and address at all
      return GeoErrorsCode.MissingLocation;
    }

    if (!hasGeolocation && hasSomeFullAddress) {
      // No Geolocation but some address field exist (but not basic address field)
      return GeoErrorsCode.GeocodingError;
    }
    // Location entered by hand or at least one basic entry with geolocation
    return null;
  } else if (!hasGeolocation) {
    // Wrong address entered
    return GeoErrorsCode.GeocodingError;
  }
  return null;
};

export const filterDuplicatesById = <T extends { id: string }>(objects: T[]): T[] => {
  const listAsset: Record<string, boolean> = {};
  return objects.filter((o) => {
    if (!listAsset[o.id]) {
      listAsset[o.id] = true;
      return true;
    }
    return false;
  });
};

const processAsset =
  (
    assetsWithGeocode: AssetMarkerType[],
    assetsMissingLocation: AssetMarkerType[],
    locationsWithoutGeocode: LocationType[],
  ) =>
  (asset: AssetMarkerType) => {
    const { locations } = asset;
    if (
      locations &&
      locations.__typename === "LocationObjectTypeConnection" &&
      locations?.edges.length
    ) {
      let assetAdded = false;
      locations.edges.forEach((edge) => {
        if (
          edge?.__typename === "LocationObjectTypeEdge" &&
          edge?.node?.__typename === "LocationObjectType"
        ) {
          const { node: location } = edge;
          const errorLocation = getPossibleLocationError(location);

          if (!errorLocation) {
            // Asset to pass for the detail needed for the map
            if (!assetAdded) {
              assetsWithGeocode.push(asset);
              assetAdded = true;
            }
          } else {
            // Asset if some geolocation info are missing
            locationsWithoutGeocode.push(location);
          }
        }
      });
    } else {
      // No location passed at all
      assetsMissingLocation.push(asset);
    }
  };

export const getProcessedAssets = (assets: AssetWithLocationFragment[]) => {
  const assetsWithGeocode = [] as AssetMarkerType[];
  const assetsMissingLocation = [] as AssetMarkerType[];
  const locationsWithoutGeocode = [] as LocationType[];

  const activeAssets = assets?.filter((a) => !a.isSold);

  activeAssets?.forEach(
    processAsset(assetsWithGeocode, assetsMissingLocation, locationsWithoutGeocode),
  );

  const assetsMissingLocationFiltered = filterDuplicatesById(assetsMissingLocation);
  const locationsWithoutGeocodeFiltered = filterDuplicatesById(locationsWithoutGeocode);

  return {
    assetsWithGeocode,
    activeAssetsCount: activeAssets?.length,
    assetMissingLocationsCount: assetsMissingLocationFiltered.length,
    locationsWithoutGeocodeCount: locationsWithoutGeocodeFiltered.length,
  };
};

export const getGeoMarkers = (assets: AssetMarkerType[]): GeoMarker[] =>
  assets.reduce((acc: GeoMarker[], asset) => {
    asset?.locations?.edges
      .map((e) => e.node)
      .filter((l) => l.latitude != null && l.longitude != null)
      .forEach((l) => {
        acc.push({
          id: acc.length,
          latitude: l.latitude as number,
          longitude: l.longitude as number,
        });
      });
    return acc;
  }, []);

/**
 * Returns map markers from arena positions. That is, a list of arenas each with a list of positions
 * each with a list of assets and each asset with a list of locations making for one well indented
 * for loop.
 *
 * @param arenaPositions The list of arenas -> positions -> assets -> locations.
 * @returns A list of map markers (with asset id, position id, lat/lon).
 */
export const getMapMarkers = (arenaPositions: ArenaPositions) => {
  const mapMarkers: GeoMarkerWithPositionId[] = [];

  arenaPositions.forEach((arenaPositions) =>
    arenaPositions.positions.forEach((position) =>
      position.assets.forEach((asset) =>
        asset.locations.forEach((location) => {
          // It doesn't make sense to push a location without lat/long
          if (location?.latitude && location?.longitude) {
            mapMarkers.push({
              id: asset.assetId,
              positionId: position.positionId,
              latitude: location?.latitude,
              longitude: location?.longitude,
            });
          }
        }),
      ),
    ),
  );

  return mapMarkers;
};

/**
 * Returns a stable string key for a list of markers so that the same set of pins
 * (in any order) always yields the same key. Used to avoid unnecessary map
 * remounts or re-fits when the underlying data is unchanged.
 *
 * @param markers Map markers (with id, positionId, lat/lon).
 * @returns A deterministic string key for the marker set.
 */
export const getStableMarkersKey = (markers: GeoMarkerWithPositionId[]): string => {
  const sorted = [...markers].sort((a, b) => {
    const pa = `${a.positionId ?? ""}-${a.id}-${a.latitude}-${a.longitude}`;
    const pb = `${b.positionId ?? ""}-${b.id}-${b.latitude}-${b.longitude}`;
    return pa.localeCompare(pb);
  });
  return JSON.stringify(sorted);
};

/**
 * Adapter that converts assetsWithLocations to GeoMarkers.
 *
 * @param assetsWithLocations To convert to geo markers (may be undefined, null, empty).
 * @returns GeoMarkers if passed assets with locations, else an empty array.
 */
export const convertToGeoMarkers = (
  assetsWithLocations: AssetWithLocationFragment[] | undefined,
): GeoMarker[] => {
  if (assetsWithLocations) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { assetsWithGeocode } = useMemo(
      () => getProcessedAssets(assetsWithLocations),
      [assetsWithLocations],
    );

    return getGeoMarkers(assetsWithGeocode);
  }

  return [];
};

/**
 * Helper function that counts of the number of assets given positions with assets.
 * @param items - An object containing positions and their respective assets.
 * @param currency - Optional currency code to filter positions. If not specified, all positions are considered.
 * @returns The total count of assets across all positions, filtered by currency if specified.
 */
function getAssetCountFromPositionWithAssets(
  items: PositionsListWithAssetLocations,
  currency?: CurrencyCode,
): number {
  return items.positions.reduce((count, item) => {
    // Count assets if currency is undefined, or matches the item's currency
    return count + (!currency || item.currency === currency ? item?.assets.length || 0 : 0);
  }, 0);
}
/**
 * Returns a count of the total assets. This takes an array of position arrays.
 * @param items - Array of position list objects, each containing positions and assets.
 * @param currency - Optional currency code to filter the count of assets. If omitted, all assets are counted.
 * @returns The aggregate count of assets across all provided position lists, filtered by currency if specified.
 */
export const getAssetCount = (
  items: PositionsListWithAssetLocations[] = [],
  currency?: CurrencyCode,
): number =>
  items.reduce((count, item) => count + getAssetCountFromPositionWithAssets(item, currency), 0);
